import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Grid, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import dayjs from 'dayjs';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ControlledField,
  DEFAULT_LANGUAGE,
  DatePickerInputField,
  LanguageField,
  OfferData,
  OfferRide,
  OfferRideInfo,
  Trip,
  colors,
  getDateLongFormatWithLocale,
} from 'remote/global';
import { CreateRideOfferEnum } from 'utils/data/enums';
import { v4 as uuidv4 } from 'uuid';
import { AddCircle } from '@mui/icons-material';
import { useState } from 'react';
import AddNewClientModal from '@/components/modals/AddNewClientModal';
import SupplierSelectField from './CreateRideOffersForm/fields/SupplierSelectField';
import { RideSupplierSchemaType, rideSupplierSchema } from './types';

const defaultValidUntil = dayjs().add(7, 'day').toISOString();

function AssignRideSupplierForm({
  onValid,
  onBack,
  defaultValues,
  isCreateOfferVariant,
}: {
  onValid: SubmitHandler<RideSupplierSchemaType>;
  onBack: (data: RideSupplierSchemaType) => void;
  defaultValues: RideSupplierSchemaType | undefined;
  isCreateOfferVariant?: boolean;
}) {
  const [isAddNewClientModalOpen, setIsAddNewClientModalOpen] = useState(false);
  const tInstance = useTranslation();
  const { t, i18n } = tInstance;
  const formInstance = useForm<RideSupplierSchemaType>({
    defaultValues: {
      rideOfferType: isCreateOfferVariant
        ? CreateRideOfferEnum.SEND_OFFER
        : CreateRideOfferEnum.CREATE_ORDER,
      validUntil: defaultValidUntil,
      clientContactEmail: '',
      clientId: '' as any,
      returnScheduleItem: true,
      operatorOfferUuids: [],
      selectedOperatorOfferUuid: '',
      trips: [],
      lang: DEFAULT_LANGUAGE,
      ...defaultValues,
    },
    resolver: zodResolver(rideSupplierSchema),
  });
  const {
    handleSubmit,
    getValues,
    control,
    watch,
    setValue,
    formState: { isSubmitting, isLoading },
  } = formInstance;
  const trips = watch('trips');
  const rideOfferType = watch('rideOfferType');
  const operatorOfferUuids = watch('operatorOfferUuids');

  const getAvailableTrips = () => {
    if (trips == null || !Array.isArray(trips) || operatorOfferUuids == null) return [];

    return trips.filter((item) => {
      return item.operatorOffers.some((offer) => operatorOfferUuids.includes(offer.uuid));
    });
  };
  const availableTrips = getAvailableTrips();

  return (
    <>
      <form onSubmit={handleSubmit(onValid)} style={{ width: '100%' }}>
        <Grid container item spacing={6} xs={12}>
          <Grid container item xs={12} md={7} gap={3} alignItems='center'>
            {availableTrips.map((trip) => {
              return trip.operatorOffers.map((offer) => {
                if (!operatorOfferUuids.includes(offer.uuid)) return null; // do not show offers that are not selected
                return (
                  <OfferData
                    key={uuidv4()}
                    isOperatorPrice
                    offer={
                      {
                        ...defaultValues,
                        ...offer,
                        trips: availableTrips as Trip[],
                      } as any
                    }
                    mappedRideInfo={trip.rides.map((ride, rideIndex) => {
                      const showRideCount = trip.rides.length > 1;
                      return (
                        <Box key={uuidv4()}>
                          {showRideCount && (
                            <Typography
                              sx={{
                                color: colors.uiBlue600,
                                fontSize: '16px',
                                fontWeight: 700,
                                mb: '10px',
                                pl: '1.75rem',
                              }}
                            >
                              {t('terms.attributes.ride.rideNo', { rideNo: rideIndex + 1 })}
                            </Typography>
                          )}
                          <OfferRideInfo selectedRide={ride as OfferRide} type={trip.type} />
                        </Box>
                      );
                    })}
                  />
                );
              });
            })}
          </Grid>
          <Grid item xs={12} md={5} gap={2} sx={{ position: 'relative' }}>
            <Box sx={{ position: 'sticky', top: '2rem', left: 0 }}>
              <Grid item xs={12} sx={{ textAlign: 'center', mb: '1rem' }}>
                <Typography sx={{ fontSize: '30px', fontWeight: 600, mt: '0.5rem' }}>
                  {t('OrderView.preview.title')}
                </Typography>
                <Typography
                  sx={{ fontSize: '16px', fontWeight: 400, color: colors.gray1, mt: '1rem' }}
                >
                  {t('OrderView.preview.subtitle')}
                </Typography>
              </Grid>
              <Grid container item xs={12}>
                <SupplierSelectField
                  form={formInstance}
                  isOptional={rideOfferType === CreateRideOfferEnum.SEND_OFFER}
                />
                <Button
                  variant='text'
                  onClick={() => setIsAddNewClientModalOpen((p) => !p)}
                  sx={{ mb: 1, textDecoration: 'none', p: 0 }}
                >
                  <AddCircle sx={{ mr: 1 }} />
                  {t('OrderView.addNewClient')}
                </Button>
                <ControlledField
                  control={control}
                  name='clientContactEmail'
                  translationKey={
                    rideOfferType === CreateRideOfferEnum.SEND_OFFER
                      ? 'clientContactEmail'
                      : 'clientContactEmailOptional'
                  }
                />
                {rideOfferType === CreateRideOfferEnum.SEND_OFFER && (
                  <>
                    <LanguageField form={formInstance} name='lang' translationKey='offerLang' />
                    <ControlledField
                      control={control}
                      name='validUntil'
                      render={(renderProps) => (
                        <DatePickerInputField
                          {...renderProps}
                          fieldProps={{
                            disablePast: true,
                            format: getDateLongFormatWithLocale(i18n.language),
                          }}
                        />
                      )}
                    />
                  </>
                )}
              </Grid>
              <Stack direction='row' justifyContent='space-between' spacing={1} my='2rem'>
                <Button
                  type='button'
                  onClick={() => onBack(getValues())}
                  variant='outlined'
                  disabled={isSubmitting || isLoading}
                >
                  {t('back')}
                </Button>
                <Button
                  type='submit'
                  disabled={isSubmitting || isLoading}
                  variant='contained'
                  sx={{ display: 'flex', maxWidth: 300 }}
                >
                  {t('OrderView.preview.send')}
                </Button>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </form>
      <AddNewClientModal
        open={isAddNewClientModalOpen}
        setOpen={setIsAddNewClientModalOpen}
        onClose={() => setIsAddNewClientModalOpen(false)}
        onSubmitSuccess={(data) => {
          setValue('clientId', data.id);
          setValue('clientContactEmail', data.contactInfo.email ?? data.companyEmail);
        }}
      />
    </>
  );
}

export default AssignRideSupplierForm;
